import { useSession } from 'hooks/useSession';
import React, { useState, useEffect, useMemo } from 'react';
import { fetchWrapper } from 'utils/apiFetch';

const MainWalletReports = () => {
  // State management
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [sortField, setSortField] = useState('date');
  const [sortDirection, setSortDirection] = useState('desc');
  const [selectedRows, setSelectedRows] = useState([]);
    const {userid} = useSession();
  // API call to fetch wallet reports
  const fetchWalletReports = async () => {
    try {
      const response = await fetchWrapper(`https://pro.mokshiq.in/main_wallet_report?user=${userid}&filter_date=2025-01-02&end_date=2025-10-24`);
     
      return response;
    } catch (error) {
      console.error('API Error:', error);
      throw error;
    }
  };

  // Fetch data from API
  useEffect(() => {
    const fetchReports = async () => {
      try {
        setLoading(true);
        const response = await fetchWalletReports();
        setReports(response.data || []);
      } catch (err) {
        setError('Failed to fetch wallet reports');
        console.error('Error fetching reports:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchReports();
  }, []);

  // Filter and search functionality
  const filteredReports = useMemo(() => {
    return reports.filter(report =>
      Object.values(report).some(value =>
        value.toString().toLowerCase().includes(searchTerm.toLowerCase())
      )
    );
  }, [reports, searchTerm]);

  // Sort functionality
  const sortedReports = useMemo(() => {
    return [...filteredReports].sort((a, b) => {
      const aValue = a[sortField];
      const bValue = b[sortField];
      
      if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
  }, [filteredReports, sortField, sortDirection]);

  // Pagination
  const paginatedReports = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    return sortedReports.slice(startIndex, startIndex + itemsPerPage);
  }, [sortedReports, currentPage, itemsPerPage]);

  const totalPages = Math.ceil(sortedReports.length / itemsPerPage);

  // Handle sorting
  const handleSort = (field) => {
    if (field === sortField) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  // Handle row selection
  const handleRowSelect = (id) => {
    setSelectedRows(prev =>
      prev.includes(id)
        ? prev.filter(rowId => rowId !== id)
        : [...prev, id]
    );
  };

  const handleSelectAll = () => {
    if (selectedRows.length === paginatedReports.length) {
      setSelectedRows([]);
    } else {
      setSelectedRows(paginatedReports.map(report => report.id));
    }
  };

  // Status badge styling
  const getStatusBadge = (status) => {
    const statusClasses = {
      'success': 'badge bg-success',
      'pending': 'badge bg-warning',
      'failed': 'badge bg-danger',
      'processing': 'badge bg-primary'
    };
    return statusClasses[status?.toLowerCase()] || 'badge bg-secondary';
  };

  // Type badge styling
  const getTypeBadge = (type) => {
    const typeClasses = {
      'debit': 'badge text-outline-danger',
      'credit': 'badge text-outline-success',
      'load_wallet': 'badge text-outline-primary'
    };
    return typeClasses[type?.toLowerCase()] || 'badge text-outline-secondary';
  };
  if (loading) {
    return (
      <main>
        <div className="container-fluid">
          <div className="row">
            <div className="col-12">
              <div className="card">
                <div className="card-body text-center">
                  <div className="app-loader mb-4">
                    <span></span>
                    <span></span>
                    <span></span>
                    <span></span>
                    <span></span>
                  </div>
                  <p className="text-secondary">Loading wallet reports...</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
    );
  }

  if (loading) {
    return (
      <main>
        <div className="container-fluid">
          <div className="row">
            <div className="col-12">
              <div className="card">
                <div className="card-body text-center">
                  <div className="app-loader mb-4">
                    <span></span>
                    <span></span>
                    <span></span>
                    <span></span>
                    <span></span>
                  </div>
                  <p className="text-secondary">Loading wallet reports...</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
    );
  }

  if (error) {
    return (
      <main>
        <div className="container-fluid">
          <div className="row">
            <div className="col-12">
              <div className="card">
                <div className="card-body text-center">
                  <i className="iconoir-warning-triangle f-s-48 text-danger mb-3"></i>
                  <h5 className="text-danger">{error}</h5>
                  <button 
                    onClick={() => window.location.reload()} 
                    className="btn btn-primary mt-3"
                  >
                    Retry
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
    );
  }

  return (
    <main>
      <div className="container-fluid">
        {/* Breadcrumb */}
        <div className="row m-1">
          <div className="col-12">
            <h4 className="main-title">Wallet Reports</h4>
            <ul className="app-line-breadcrumbs mb-3">
              <li className="">
                <a className="f-s-14 f-w-500" href="#">
                  <span>
                    <i className="ph-duotone ph-wallet f-s-16"></i> Reports
                  </span>
                </a>
              </li>
              <li className="active">
                <a className="f-s-14 f-w-500" href="#">Wallet Reports</a>
              </li>
            </ul>
          </div>
        </div>

        {/* Wallet Reports Table */}
        <div className="row advance-table-section">
          <div className="col-12">
            <div className="card">
              <div className="card-header d-flex justify-content-between align-items-center">
                <div>
                  <h5>Wallet Transaction Reports</h5>
                  <p className="text-secondary f-s-14 mb-0">Total {sortedReports.length} transactions found</p>
                </div>
                
                {/* Search and Controls */}
                <div className="d-flex gap-2 align-items-center">
                  <div className="position-relative">
                    <input
                      type="text"
                      className="form-control"
                      placeholder="Search transactions..."
                      value={searchTerm}
                      onChange={(e) => {
                        setSearchTerm(e.target.value);
                        setCurrentPage(1);
                      }}
                      style={{ paddingLeft: '35px', width: '250px' }}
                    />
                    <i className="iconoir-search position-absolute" style={{ left: '12px', top: '50%', transform: 'translateY(-50%)' }}></i>
                  </div>
                  
                  <select
                    className="form-select"
                    value={itemsPerPage}
                    onChange={(e) => {
                      setItemsPerPage(Number(e.target.value));
                      setCurrentPage(1);
                    }}
                    style={{ width: '80px' }}
                  >
                    <option value={5}>5</option>
                    <option value={10}>10</option>
                    <option value={25}>25</option>
                    <option value={50}>50</option>
                  </select>
                </div>
              </div>

              {selectedRows.length > 0 && (
                <div className="card-body border-bottom">
                  <div className="d-flex justify-content-between align-items-center">
                    <span className="text-secondary f-s-14">{selectedRows.length} item(s) selected</span>
                    <div className="d-flex gap-2">
                      <button className="btn btn-danger btn-sm">
                        <i className="ti ti-trash"></i> Delete Selected
                      </button>
                      <button className="btn btn-primary btn-sm">
                        <i className="ti ti-download"></i> Export Selected
                      </button>
                    </div>
                  </div>
                </div>
              )}

              <div className="card-body p-0">
                <div className="table-responsive app-scroll">
                  <table className="table table-bottom-border table-box-hover align-middle mb-0">
                    <thead>
                      <tr>
                        <th>
                          <label className="check-box">
                            <input
                              type="checkbox"
                              checked={selectedRows.length === paginatedReports.length && paginatedReports.length > 0}
                              onChange={handleSelectAll}
                            />
                            <span className="checkmark outline-light ms-2"></span>
                          </label>
                        </th>
                        {[
                          { field: 'order_id', label: 'Order ID' },
                          { field: 'txn_id', label: 'Transaction ID' },
                          { field: 'product', label: 'Product' },
                          { field: 'type', label: 'Type' },
                          { field: 'amount', label: 'Amount' },
                          { field: 'status', label: 'Status' },
                          { field: 'created_at', label: 'Date' },
                          { field: 'commission', label: 'Commission' },
                          { field: 'opening_balance', label: 'Opening Balance' },
                          { field: 'closing_balance', label: 'Closing Balance' }
                        ].map(({ field, label }) => (
                          <th
                            key={field}
                            onClick={() => handleSort(field)}
                            style={{ cursor: 'pointer' }}
                            className="user-select-none"
                          >
                            <div className="d-flex align-items-center">
                              {label}
                              {/* <div className="ms-1">
                                <i className={`ti ti-chevron-up f-s-12 ${sortField === field && sortDirection === 'asc' ? 'text-primary' : 'text-secondary'}`}></i>
                                <br />
                                <i className={`ti ti-chevron-down f-s-12 ${sortField === field && sortDirection === 'desc' ? 'text-primary' : 'text-secondary'}`} style={{ marginTop: '-8px' }}></i>
                              </div> */}
                            </div>
                          </th>
                        ))}
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {paginatedReports.map((report) => (
                        <tr 
                          key={report.id} 
                          className={selectedRows.includes(report.id) ? 'bg-light-primary' : ''}
                        >
                          <td>
                            <label className="check-box">
                              <input
                                type="checkbox"
                                checked={selectedRows.includes(report.id)}
                                onChange={() => handleRowSelect(report.id)}
                              />
                              <span className="checkmark outline-light ms-2"></span>
                            </label>
                          </td>
                          <td>
                            <span className="f-w-500 text-primary">#{report.order_id}</span>
                          </td>
                          <td>
                            <span className="font-monospace f-s-13 text-secondary">{report.txn_id || 'N/A'}</span>
                          </td>
                          <td>
                            <span className="f-w-500 text-dark text-capitalize">{report.product}</span>
                          </td>
                          <td>
                            <span className={getTypeBadge(report.type)}>
                              {report.type?.toUpperCase()}
                            </span>
                          </td>
                          <td>
                            <div>
                              <div className="f-w-600 text-dark">₹{parseFloat(report.amount).toLocaleString()}</div>
                              <div className="f-s-12 text-secondary">INR</div>
                            </div>
                          </td>
                          <td>
                            <span className={getStatusBadge(report.status)}>
                              {report.status?.toUpperCase()}
                            </span>
                          </td>
                          <td className="f-w-500 text-dark">
                            {report.created_at}
                          </td>
                          <td className="f-w-500 text-success">
                            {report.commission && !isNaN(parseFloat(report.commission)) ? `₹${parseFloat(report.commission).toFixed(2)}` : 'N/A'}
                          </td>
                          <td className="f-w-500 text-info">
                            {report.opening_balance && !isNaN(parseFloat(report.opening_balance)) ? `₹${parseFloat(report.opening_balance).toFixed(2)}` : '₹0.00'}
                          </td>
                          <td className="f-w-500 text-warning">
                            {report.closing_balance && !isNaN(parseFloat(report.closing_balance)) ? `₹${parseFloat(report.closing_balance).toFixed(2)}` : '₹0.00'}
                          </td>
                          <td>
                            <div className="d-flex gap-1">
                              <button className="btn btn-primary icon-btn b-r-4" type="button" title="View">
                              <i class="iconoir-eye"></i>
                              </button>
                              <button className="btn btn-success icon-btn b-r-4" type="button" title="Edit">
                              <i class="iconoir-edit"></i>
                              </button>
                              <button className="btn btn-danger icon-btn b-r-4" type="button" title="Delete">
                              <i class="iconoir-trash"></i>
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Pagination */}
              <div className="card-footer">
                <div className="row align-items-center">
                  <div className="col-md-6">
                    <p className="mb-0 text-secondary f-s-14">
                      Showing {((currentPage - 1) * itemsPerPage) + 1} to{' '}
                      {Math.min(currentPage * itemsPerPage, sortedReports.length)} of{' '}
                      {sortedReports.length} entries
                    </p>
                  </div>
                  <div className="col-md-6">
                    <nav aria-label="Page navigation">
                      <ul className="pagination justify-content-end mb-0">
                        <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                          <button
                            className="page-link"
                            onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                            disabled={currentPage === 1}
                          >
                            <i className="ti ti-chevron-left"></i>
                          </button>
                        </li>
                        
                        {[...Array(totalPages)].map((_, i) => (
                          <li key={i} className={`page-item ${currentPage === i + 1 ? 'active' : ''}`}>
                            <button
                              className="page-link"
                              onClick={() => setCurrentPage(i + 1)}
                            >
                              {i + 1}
                            </button>
                          </li>
                        ))}
                        
                        <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                          <button
                            className="page-link"
                            onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                            disabled={currentPage === totalPages}
                          >
                            <i className="ti ti-chevron-right"></i>
                          </button>
                        </li>
                      </ul>
                    </nav>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </main>
  );
};

export default MainWalletReports;